import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["close", "children", "title", "footer", "footerType", "renderFooter", "footerCollapseCount", "titleAlign", "contentAlign", "className", "maskClass", "contentClass", "contentStyle", "contentTransitionType", "maskTransitionTimeout", "contentTransitionTimeout", "extra"];
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { cls, componentWrapper } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import Masking from '../masking';
import { alert, confirm, open } from './methods';
import { useSystem } from '../_helpers';
export * from './methods';
var Dialog = /*#__PURE__*/forwardRef(function (props, ref) {
  var system = useSystem();
  var nowSystem = props.platform || system;

  var _props$close = props.close,
      close = _props$close === void 0 ? function () {} : _props$close,
      children = props.children,
      title = props.title,
      _props$footer = props.footer,
      footer = _props$footer === void 0 ? [] : _props$footer,
      _props$footerType = props.footerType,
      footerType = _props$footerType === void 0 ? 'grid' : _props$footerType,
      renderFooter = props.renderFooter,
      _props$footerCollapse = props.footerCollapseCount,
      footerCollapseCount = _props$footerCollapse === void 0 ? 3 : _props$footerCollapse,
      _props$titleAlign = props.titleAlign,
      titleAlign = _props$titleAlign === void 0 ? nowSystem === 'android' ? 'left' : 'center' : _props$titleAlign,
      _props$contentAlign = props.contentAlign,
      contentAlign = _props$contentAlign === void 0 ? nowSystem === 'android' ? 'left' : 'center' : _props$contentAlign,
      className = props.className,
      maskClass = props.maskClass,
      contentClass = props.contentClass,
      contentStyle = props.contentStyle,
      _props$contentTransit = props.contentTransitionType,
      contentTransitionType = _props$contentTransit === void 0 ? nowSystem === 'android' ? 'fade' : 'fade-scale' : _props$contentTransit,
      _props$maskTransition = props.maskTransitionTimeout,
      maskTransitionTimeout = _props$maskTransition === void 0 ? 300 : _props$maskTransition,
      _props$contentTransit2 = props.contentTransitionTimeout,
      contentTransitionTimeout = _props$contentTransit2 === void 0 ? 450 : _props$contentTransit2,
      extra = props.extra,
      restProps = _objectWithoutPropertiesLoose(props, _excluded);

  var maskingRef = useRef(null);
  var contentRef = useRef(null);
  useImperativeHandle(ref, function () {
    var _maskingRef$current, _maskingRef$current2, _maskingRef$current3;

    return {
      dom: ((_maskingRef$current = maskingRef.current) == null ? void 0 : _maskingRef$current.dom) || null,
      mask: ((_maskingRef$current2 = maskingRef.current) == null ? void 0 : _maskingRef$current2.mask) || null,
      content: contentRef.current,
      setCloseScene: ((_maskingRef$current3 = maskingRef.current) == null ? void 0 : _maskingRef$current3.setCloseScene) || function () {}
    };
  });

  function handleFooterBtnClick(e, btn) {
    e.preventDefault();
    e.stopPropagation();

    if (btn.disabled) {
      return;
    }

    var result = btn.onClick ? btn.onClick(e) : null;

    if (!result || typeof result === 'boolean') {
      !result && close(e);
    } else if (result && result.then) {
      result.then(function (res) {
        return !res && close(e);
      });
    }
  }

  function renderDialog(_ref) {
    var prefixCls = _ref.prefixCls;
    var prefix = prefixCls + "-dialog";
    return /*#__PURE__*/React.createElement(Masking, _extends({
      close: close,
      className: cls(prefix, nowSystem, className),
      ref: maskingRef,
      maskClass: cls(prefix + "-mask", maskClass),
      contentTransitionType: contentTransitionType,
      contentClass: cls(prefix + "-container"),
      maskTransitionTimeout: maskTransitionTimeout,
      contentTransitionTimeout: contentTransitionTimeout
    }, restProps), /*#__PURE__*/React.createElement("div", {
      className: cls(prefix + "-content", prefix + "-part", nowSystem, contentClass),
      style: contentStyle,
      ref: contentRef
    }, title ? /*#__PURE__*/React.createElement("div", {
      className: cls(prefix + "-header", prefix + "-part", titleAlign, nowSystem, "system-" + system, {
        'only-title': !children
      })
    }, title) : null, children ? /*#__PURE__*/React.createElement("div", {
      className: cls(prefix + "-body", prefix + "-part", contentAlign, nowSystem, {
        'sub-title': !title
      })
    }, children) : null, renderFooter || footer.length ? /*#__PURE__*/React.createElement("div", {
      className: cls(prefix + "-footer", prefix + "-part", nowSystem, "type-" + footerType, {
        collapsed: footer.length >= footerCollapseCount
      })
    }, renderFooter ? renderFooter() : footer.map(function (btn, index) {
      return /*#__PURE__*/React.createElement("div", {
        key: index,
        className: cls('dialog-footer-button', btn.className, "system-" + system, {
          disabled: btn.disabled
        }),
        onClick: function onClick(e) {
          return handleFooterBtnClick(e, btn);
        }
      }, btn.content);
    })) : null, extra ? /*#__PURE__*/React.createElement("span", null, extra) : null));
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, renderDialog);
});
export function methodsGenerator(Comp) {
  return {
    /**
     * 打开确认框(含一个确认按钮)
     * @param {AlertOptions & DialogProps} config 配置
     * @returns {{ close: () => void; update: (newConfig: AlertOptions & DialogProps) => void; }}
     */
    alert: alert(Comp),

    /**
     * 打开提示框(含一个确认按钮和一个取消按钮)
     * @param {ConfirmOptions & AlertOptions} config 配置
     * @returns {{ close: () => void; update: (newConfig: ConfirmOptions & AlertOptions) => void; }}
     */
    confirm: confirm(Comp),

    /**
     * 打开常规对话框
     * @param {DialogProps} config 配置
     * @returns {{ close: () => void; update: (newConfig: DialogProps) => void; }}
     */
    open: open(Comp)
  };
}
/**
 * 模态对话框，在浮层中显示，引导用户进行相关操作。默认做了防滚动穿透处理，如果弹层内容中需要滚动，则需将滚动容器传入`getScrollContainer`属性以在未滚动到顶部或底部时释放滚动。
 * @type 反馈
 * @name 对话框
 */

export default componentWrapper(Dialog, methodsGenerator(Dialog));